import React, { useState, useRef } from "react";
import { jwtDecode } from "jwt-decode";
import _ from "lodash";
import LoginDialog from "./AuthComponents/LoginDialog";
import TimeoutDialog from "./AuthComponents/TimeOutDialog";
import type { QueryFunctionContext } from "@tanstack/react-query";

// change the following const value if you want to have public apps
const REQUIRE_GUEST_TOKEN = false;
// set this const if you want the JWT token automatically refresh after % of token validity is elapsed
const REFRESH_WHEN_ELAPSED = 0.3;
// set this const so an alert is shown if below % of Token validity time is over (0.5 for 50%)
const ASK_KEEP_LOGIN_IF_ELAPSED = 0.9;

export type AuthContextType = {
  showLogin: () => void;
  hideLogin: () => void;
  LoggedInUserID: number;
  LoggedInUserName: string;
  LoggedInUserProfile: {
    name: string;
    id: number;
    department: string;
    departmentId: number;
    level: string;
    roles: string[];
  };
  AccessibleApps: unknown[];
  Token: string | undefined;
  AuthenticatedServerCall: <T = unknown>(
    url: string,
    method: string,
    body?: unknown,
    options?: { signal?: AbortSignal }
  ) => Promise<T>;
  logout: () => void;
  getRequest: <T = unknown>(
    context: QueryFunctionContext<readonly unknown[]>
  ) => Promise<T>;
};

const AuthContext = React.createContext<AuthContextType | undefined>(undefined);

export function AuthProvider(props: any) {
  const [LoginVisible, setLoginVisible] = React.useState(false);
  const [LoggedInUserID, setLoggedInUserID] = React.useState<number>(-1);
  const [LoggedInUserName, setLoggedInUserName] = React.useState<string>("");
  const [LoggedInUserProfile, setLoggedInUserProfile] = useState<
    AuthContextType["LoggedInUserProfile"]
  >({
    name: "",
    id: -1,
    department: "",
    departmentId: -1,
    level: "",
    roles: [],
  });
  const [initialLoad, setInitialLoad] = useState(true);
  const [initialLoadFailed, setInitialLoadFailed] = useState(false);
  const [AccessibleApps, setAccessibleApps] = useState<unknown[]>([]);
  const [loginState, setLoginState] = useState("");
  //---
  const Token = useRef<string>();
  const tokenExpiresAt = useRef();
  const IdleTimer = useRef<NodeJS.Timeout>();
  const refreshTokenWithNextRequest = useRef<boolean>();
  const refreshTokenTimer = useRef<NodeJS.Timeout>();
  //----
  const [openTimeOutDialog, setOpenTimeOutDialog] = useState(false);
  function showLogin() {
    setLoginVisible(true);
  }
  function hideLogin() {
    setLoginVisible(false);
  }
  function logout() {
    changeToken(undefined);
  }

  function changeToken(newToken: string | undefined) {
    //clean up
    if (IdleTimer.current !== undefined) {
      clearTimeout(IdleTimer.current);
    }
    if (refreshTokenTimer.current !== undefined) {
      clearTimeout(refreshTokenTimer.current);
    }
    refreshTokenWithNextRequest.current = false;
    //---
    if (newToken === undefined) {
      Token.current = undefined;
      tokenExpiresAt.current = undefined;
      setAccessibleApps([]);
      setLoggedInUserID(-1);
      setLoggedInUserName("");
      //change the value for the get token to kick in
      setInitialLoad(true);
      return;
    }
    let decodedToken: any;
    try {
      decodedToken = jwtDecode(newToken);
    } catch (error) {
      console.error('Failed to decode JWT token:', error);
      // Invalid token - treat as logged out
      Token.current = undefined;
      tokenExpiresAt.current = undefined;
      setAccessibleApps([]);
      setLoggedInUserID(-1);
      setLoggedInUserName("");
      setInitialLoad(true);
      setLoginState("Failed");
      return;
    }
    Token.current = newToken;
    //@ts-ignore
    tokenExpiresAt.current = decodedToken.exp * 1000;
    IdleTimer.current = setTimeout(() => {
      setOpenTimeOutDialog(true);
    }, (decodedToken.exp - decodedToken.iat) * 1000 * ASK_KEEP_LOGIN_IF_ELAPSED);
    refreshTokenTimer.current = setTimeout(() => {
      refreshTokenWithNextRequest.current = true;
    }, (decodedToken.exp - decodedToken.iat) * 1000 * REFRESH_WHEN_ELAPSED);
  }

  async function authenticateUser(username: String, password: String) {
    try {
      username = username.toLowerCase();
      setLoginState("InProgress");
      const headers = {
        Accept: "application/json",
        "Content-Type": "application/json",
      };
      const rawResult = await fetch(`${import.meta.env.VITE_APIURL}login`, {
        method: "POST",
        headers,
        body: JSON.stringify({
          ClockNo: username,
          password: password,
        }),
      });
      if (!rawResult.ok) {
        //reset the token so will get new guest token
        changeToken(undefined);
        if (rawResult.status === 429) {
          setLoginState("RateLimited");
          return;
        }
        setLoginState("Failed");
        throw new Error(rawResult.statusText);
      }
      const result = await rawResult.json();
      changeToken(result["token"]);
      //---
      setLoggedInUserProfile(result.userProfile);
      setLoggedInUserID(result.userProfile.id);
      setLoggedInUserName(result.userProfile.name);
      setLoginState("Success");
      hideLogin();
    } catch (exception) {
      setLoginState("Failed");
      setLoggedInUserID(-1);
      setLoggedInUserName("");
      changeToken(undefined);
    }
  }

  async function refreshToken() {
    try {
      console.debug("refreshing token");

      // get a new token
      const headers = {
        Accept: "application/json",
        "Content-Type": "application/json",
        Authorization: `Bearer ${Token.current}`,
      };
      const rawResult = await fetch(
        `${import.meta.env.VITE_APIURL}refreshToken`,
        {
          method: "POST",
          headers,
        }
      );
      if (!rawResult.ok) {
        console.debug(
          "failed to refresh token, try again with the next request"
        );
        //keep the current token
        throw new Error(rawResult.statusText);
      }
      const result = await rawResult.json();
      changeToken(result["token"]);
    } catch (e) {
      //TODO: send to server error log api
      console.log(e);
    }
  }

  //---at initial load we will get a guest token and this will automatically cause fetching guest apps
  React.useEffect(() => {
    async function initialTokenFetch() {
      try {
        //console.log("in guest token fetch");
        //get guest token then get accessible (public apps) and also set initial load to false
        const headers = {
          Accept: "application/json",
          "Content-Type": "application/json",
        };
        const result = await fetch(
          `${import.meta.env.VITE_APIURL}guesttoken`,
          {
            method: "POST",
            headers,
          }
        );
        if (!result.ok) throw new Error(result.statusText);
        const res = await result.json();
        changeToken(res["token"]);
        setLoggedInUserID(-1);
        setLoggedInUserName("");
        setInitialLoad(false);
      } catch (exception) {
        console.log(exception);
        setInitialLoadFailed(true);
      }
    }
    if (initialLoad) {
      if (REQUIRE_GUEST_TOKEN) {
        try {
          initialTokenFetch();
        } catch (exception) {
          console.log(exception);
          setInitialLoadFailed(true);
        }
      } else {
        //in this case show the login dialog straight away
        showLogin();
      }
    }
  }, [initialLoad]);

  //if the LoggedInUserId changes we re fetch the accessible apps
  React.useEffect(() => {
    async function getApps() {
      const getAccessibleApps = async function () {
        if (!Token.current) {
          return;
        }
        const headers = {
          Accept: "application/json",
          "Content-Type": "application/json",
          Authorization: `Bearer ${Token.current}`,
        };
        const result = await fetch(
          `${import.meta.env.VITE_APIURL}accessibleapps`,
          {
            headers,
          }
        );
        if (!result.ok) {
          setInitialLoadFailed(true);
          throw new Error(result.statusText);
        }
        const res = await result.json();
        let tempApps = res.filter(
          (app: any) => app.ParentID && app.ParentID === -1
        );
        tempApps.forEach((appGroup: any) => {
          let appsForGroup = res.filter((app: any) => {
            return app.ParentID === appGroup.ApplicationID;
          });
          if (appsForGroup.length > 0) {
            appGroup.ShowInNavigationTree = true;
            appGroup.childApps = appsForGroup;
          } else {
            appGroup.ShowInNavigationTree = false; //empty group no need to display
          }
        });
        //---add all other apps (with no parent)
        tempApps.push(
          ...res.filter((app: any) => !app.ParentID || app.ParentID === null)
        );
        setAccessibleApps(
          tempApps.sort((appA: any, appB: any) => appA.AppOrder - appB.AppOrder)
        );

        return;
      };
      try {
        await getAccessibleApps();
      } catch (exception) {
        console.error("Failed to get accessible apps:", exception);
        setInitialLoadFailed(true);
      }
    }
    getApps();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [LoggedInUserID]);

  // if need refresh then call for the first time and no need to call again for the interval
  var callRefresh = _.debounce(refreshToken, 5000, {
    leading: true,
    trailing: false,
  });

  async function AuthenticatedServerCall<T = unknown>(
    url: string,
    method: string,
    body: any = undefined,
    options: { signal?: AbortSignal } = {}
  ): Promise<T> {
    try {
      const response = await fetch(url, {
        method: method,
        signal: options.signal,
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          Authorization: `Bearer ${Token.current}`,
        },
        ...(body && { body: JSON.stringify(body) }),
      });
      if (response.ok) {
        // if token is gonna expire then ask for a new one
        if (refreshTokenWithNextRequest.current) {
          console.debug("asking to refresh");
          callRefresh();
        }
        // 204 doesn't have any content
        if (response.status === 204) {
          return true as T;
        } else {
          return (await response.json()) as T;
        }
      } else {
        if (response.status === 401) {
          //unauthorized
          //show login dialog (in case user can/wants to login)
          logout();
          setLoginVisible(true);
          throw Error("unauthorized/relogin");
        }
        // Try to parse error message from response body
        let errorMessage = response.statusText;
        try {
          const errorData = await response.json();
          errorMessage = (errorData && errorData.error && errorData.error.message) || (errorData && errorData.message) || response.statusText;
        } catch (parseError) {
          // If parsing fails, use statusText (already set above)
          console.warn("Failed to parse error response body:", parseError);
        }
        throw Error(errorMessage);
      }
    } catch (error) {
      // Re-throw to let React Query handle the error
      throw error;
    }
  }

  // getRequest handles react-query v5 context object { queryKey, signal, meta }
  const getRequest = async <T = unknown>(
    context: QueryFunctionContext<readonly unknown[]>
  ): Promise<T> => {
    const queryKey = context.queryKey;
    const tempUrl = import.meta.env.VITE_APIURL + String(queryKey.join("/"));
    return AuthenticatedServerCall<T>(tempUrl, "get", undefined, {
      signal: context.signal,
    });
  };

  if (initialLoadFailed) {
    return <div>Failed to initialize the app</div>;
  }
  return (
    <AuthContext.Provider
      value={{
        showLogin,
        hideLogin,
        LoggedInUserID,
        LoggedInUserName,
        AccessibleApps,
        Token: Token.current,
        AuthenticatedServerCall,
        logout,
        LoggedInUserProfile,
        getRequest,
      }}
    >
      <LoginDialog
        LoginVisible={LoginVisible}
        hideLogin={hideLogin}
        authenticateUser={(username: String, password: String) =>
          authenticateUser(username, password)
        }
        loginState={loginState}
      />
      {openTimeOutDialog ? (
        <TimeoutDialog
          quit={() => {
            setOpenTimeOutDialog(false);
            logout();
            showLogin();
          }}
          refreshToken={() => {
            setOpenTimeOutDialog(false);
            refreshToken();
          }}
          time={100}
        />
      ) : null}
      {props.children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextType {
  const context = React.useContext(AuthContext);
  if (!context) {
    throw new Error(`useAuth must be used within a AuthProvider`);
  }
  return context;
}
